import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

export interface Package {
  id: string;
  name: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface PackagesApiResponse {
  isSuccess: boolean;
  message: string;
  data: Package[];
}

export interface PackagesResponse {
  packages: Package[];
  count: number;
}

export async function fetchPackages(): Promise<PackagesResponse> {
  const url = getBITSEndApiMap("fetchPackages");

  const response = await api.get<PackagesApiResponse>(url);
  return {
    packages: response.data.data,
    count: response.data.data.length,
  };
}

export function usePackagesQuery(
  options?: Omit<
    UseQueryOptions<PackagesResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<PackagesResponse, Error>({
    queryKey: ["packages"],
    queryFn: fetchPackages,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// Resource Profile interfaces and functions
export interface CreateResourceProfilePayload {
  name: string;
  resource_type: string;
  cpu_cores: number;
  ram_gb: number;
  disk_gb: number;
  max_runtime_hours: number;
  idle_timeout_minutes: number;
  cost_usd: number;
  custom_packages?: string;
  packages: string[];
  is_active: boolean;
}

export interface ResourceProfileResponse {
  isSuccess: boolean;
  message: string;
  data: {
    id: string;
    name: string;
    resource_type: string;
    cpu_cores: number;
    ram_gb: number;
    disk_gb: number;
    max_runtime_hours: number;
    idle_timeout_minutes: number;
    cost_usd: number;
    packages: string[];
    is_active: boolean;
    build_status?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export async function createResourceProfile(
  payload: CreateResourceProfilePayload,
): Promise<ResourceProfileResponse> {
  const url = getBITSEndApiMap("createResourceProfile");

  const response = await api.post<ResourceProfileResponse>(url, payload);
  return response.data;
}

export function useCreateResourceProfile(
  options?: UseMutationOptions<
    ResourceProfileResponse,
    Error,
    CreateResourceProfilePayload
  >,
) {
  return useMutation<
    ResourceProfileResponse,
    Error,
    CreateResourceProfilePayload
  >({
    mutationFn: createResourceProfile,
    ...options,
  });
}

// Fetch all resource profiles
export interface ResourceProfileData {
  id: string;
  name: string;
  resource_type: string;
  cpu_cores: number;
  ram_gb: number;
  disk_gb: number;
  max_runtime_hours: number;
  idle_timeout_minutes: number;
  cost_usd: number;
  packages: string[];
  custom_packages?: string;
  is_active: boolean;
  build_status?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceProfilesApiResponse {
  isSuccess: boolean;
  message: string;
  data: {
    data: ResourceProfileData[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
}

export interface ResourceProfilesResponse {
  profiles: ResourceProfileData[];
  count: number;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface ResourceProfileQueryParams {
  page?: number;
  limit?: number;
  sortBy?: "created_at" | "updated_at" | "name" | "cpu_cores" | "ram_gb";
  sortOrder?: "ASC" | "DESC";
  cpu_cores?: number;
  ram_gb?: number;
  disk_gb?: number;
}

export async function fetchResourceProfiles(
  params?: ResourceProfileQueryParams,
): Promise<ResourceProfilesResponse> {
  const baseUrl = getBITSEndApiMap("getResourceProfiles");
  const searchParams = new URLSearchParams();

  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.sortBy) searchParams.set("sortBy", params.sortBy);
  if (params?.sortOrder) searchParams.set("sortOrder", params.sortOrder);
  if (params?.cpu_cores)
    searchParams.set("cpu_cores", String(params.cpu_cores));
  if (params?.ram_gb) searchParams.set("ram_gb", String(params.ram_gb));
  if (params?.disk_gb) searchParams.set("disk_gb", String(params.disk_gb));

  const url = searchParams.toString()
    ? `${baseUrl}?${searchParams.toString()}`
    : baseUrl;

  const response = await api.get<ResourceProfilesApiResponse>(url);
  return {
    profiles: response.data.data.data,
    count:
      response.data.data.pagination?.totalItems ||
      response.data.data.data.length,
    pagination: response.data.data.pagination,
  };
}

export function useResourceProfilesQuery(
  params?: ResourceProfileQueryParams,
  options?: Omit<
    UseQueryOptions<ResourceProfilesResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<ResourceProfilesResponse, Error>({
    queryKey: ["resourceProfiles", params],
    queryFn: () => fetchResourceProfiles(params),
    staleTime: 3 * 60 * 1000, // 3 minutes
    ...options,
  });
}

export interface ResourceProfileByIdResponse {
  isSuccess: boolean;
  message: string;
  data: ResourceProfileData;
}

// Fetch a single resource profile by ID
export async function fetchResourceProfileById(
  resourceProfileId: string,
): Promise<ResourceProfileData> {
  const baseUrl = getBITSEndApiMap("getResourceProfiles");
  const url = `${baseUrl}/${resourceProfileId}`;
  // or construct manually if not in map:
  // const url = `/api/resource-profiles/${resourceProfileId}`;

  const response = await api.get<ResourceProfileByIdResponse>(url);
  return response.data.data;
}

// React Query hook for fetching a single profile
export function useResourceProfileByIdQuery(
  resourceProfileId?: string,
  options?: Omit<
    UseQueryOptions<ResourceProfileData, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<ResourceProfileData, Error>({
    queryKey: ["resourceProfile", resourceProfileId],
    queryFn: () => fetchResourceProfileById(resourceProfileId!),
    enabled: !!resourceProfileId, // only run when id is provided
    staleTime: 3 * 60 * 1000, // 3 minutes cache
    ...options,
  });
}

export interface UpdateResourceProfilePayload {
  id: string;
  data: CreateResourceProfilePayload;
}

export async function updateResourceProfile(
  payload: UpdateResourceProfilePayload,
): Promise<ResourceProfileResponse> {
  const url = getBITSEndApiMap("updateResourceProfile").replace(
    ":id",
    payload.id,
  );

  const response = await api.put<ResourceProfileResponse>(url, payload.data);
  return response.data;
}

export function useUpdateResourceProfile(
  options?: UseMutationOptions<
    ResourceProfileResponse,
    Error,
    UpdateResourceProfilePayload
  >,
) {
  return useMutation<
    ResourceProfileResponse,
    Error,
    UpdateResourceProfilePayload
  >({
    mutationFn: updateResourceProfile,
    ...options,
  });
}

// Delete Resource Profile
export interface DeleteResourceProfileResponse {
  isSuccess: boolean;
  message: string;
}

export async function deleteResourceProfile(
  id: string,
): Promise<DeleteResourceProfileResponse> {
  const url = getBITSEndApiMap("updateResourceProfile").replace(":id", id);

  const response = await api.delete<DeleteResourceProfileResponse>(url);
  return response.data;
}

export function useDeleteResourceProfile(
  options?: UseMutationOptions<DeleteResourceProfileResponse, Error, string>,
) {
  return useMutation<DeleteResourceProfileResponse, Error, string>({
    mutationFn: deleteResourceProfile,
    ...options,
  });
}
